<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { ATSApplicationScoringProScoring } from '@factoryfixinc/ats-interfaces';
import StarWithoutBgIcon from '@/assets/svg/conversations/star_without_bg.svg?component';
import CopilotChip from '@/components/Conversations/Message/CopilotChip.vue';
import ScoreChip from '@/components/Conversations/Application/ScoreChip.vue';
import CopilotScoreFeedback from '@/components/Conversations/Copilot/CopilotScoreFeedback.vue';
import ApplicationScoreService from '@/core/conversations/application-score/application-score.service';
import { hasApplicationScore } from '@/core/conversations/application-score/application-score.util';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import type { CriteriaColor } from '@/components/Conversations/Application/ScoreChip';
import { renderScoreMarkdown } from '@/utils/strings/render-score-markdown.util';

type Chip = { title: string; text: string; color: CriteriaColor };

// The card can be fed directly from message metadata (score/content/chips) or,
// the primary path, hydrate itself from the score detail on mount. Props win
// over the loaded detail so a caller with metadata never triggers a fetch.
const props = withDefaults(
  defineProps<{
    applicationId: number;
    jobTitle?: string;
    aiRecruiterName?: string;
    score?: number | null;
    content?: string;
    chips?: Chip[];
  }>(),
  {
    jobTitle: '',
    aiRecruiterName: 'Copilot',
  },
);

const applicationScoreService = new ApplicationScoreService();

const loadedDetail = ref<ATSApplicationScoringProScoring | undefined>();
const isLoading = ref(false);

const scoreDetail = computed(() => loadedDetail.value);

const rawScore = computed<number | null | undefined>(() =>
  props.score !== undefined ? props.score : scoreDetail.value?.score,
);
const hasScore = computed(() => hasApplicationScore(rawScore.value));
const scoreValue = computed(() => (hasScore.value ? (rawScore.value as number) : 0));
const formattedScore = computed(() =>
  hasScore.value ? ApplicationScoreService.formatScore(rawScore.value) : '',
);

const rawSummary = computed(() =>
  props.content !== undefined ? props.content : scoreDetail.value?.summary || '',
);
const summaryHtml = computed(() => renderScoreMarkdown(rawSummary.value));

const chips = computed<Chip[]>(
  () => props.chips ?? ApplicationScoreService.getScoreChipsInfo(scoreDetail.value),
);

onMounted(async () => {
  // Only fetch when the caller has not already supplied the derived content.
  if (props.score !== undefined || props.content !== undefined) {
    return;
  }

  isLoading.value = true;
  try {
    loadedDetail.value = await applicationScoreService.getScoreDetail(props.applicationId);
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Could not load the score details.');
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div
    class="rounded-bl-xl rounded-br rounded-tl-xl rounded-tr-xl border border-primary-50 bg-primary-0 p-4"
    data-test="mobile-score-blurb"
  >
    <template v-if="isLoading">
      <v-skeleton-loader boilerplate type="subtitle, paragraph" />
    </template>
    <template v-else>
      <div class="flex flex-col gap-3">
        <!-- Copilot badge + score on one row, visible-only-to-you below -->
        <div class="flex flex-col gap-3">
          <div class="flex flex-wrap items-center gap-1">
            <CopilotChip class="h-6" :ai-recruiter-name="aiRecruiterName" />

            <!-- max-w-full + truncate keep a long job title from widening the pill past
                 the card, which would otherwise force the whole thread to pan sideways
                 on narrow phones. -->
            <div
              v-if="hasScore"
              class="inline-flex h-6 max-w-full items-center gap-1.5 rounded bg-primary-300 pl-0.5 pr-1"
              data-test="mobile-score-blurb-score"
            >
              <span
                class="inline-flex h-5 shrink-0 items-center gap-0.5 rounded-sm bg-tint-0 pl-0.5 pr-1"
              >
                <StarWithoutBgIcon aria-hidden="true" class="h-4 w-4 text-primary-800" />
                <span class="text-sm font-extrabold text-shade-880">{{ formattedScore }}</span>
              </span>
              <template v-if="jobTitle">
                <span class="shrink-0 text-xs text-shade-900">for</span>
                <span class="min-w-0 truncate text-xs font-bold text-shade-900">{{
                  jobTitle
                }}</span>
              </template>
            </div>
          </div>

          <div class="flex items-center gap-1" data-test="mobile-score-blurb-visibility">
            <img src="@/assets/svg/conversations/eye.svg" alt="" class="h-4 w-4" />
            <span class="text-xs font-normal italic text-shade-800">Visible only to you</span>
          </div>
        </div>

        <!-- Summary -->
        <div
          v-if="summaryHtml"
          class="score-summary text-sm text-shade-880 [overflow-wrap:anywhere]"
          data-test="mobile-score-blurb-summary"
          v-html="summaryHtml"
        ></div>

        <!-- Criteria chips with the feedback aligned to the bottom-right. flex-wrap +
             min-w-0 let the fixed-width feedback control drop to its own line instead
             of forcing the card (and the whole thread) wider than narrow phones. -->
        <div v-if="chips.length || hasScore" class="flex flex-wrap items-end justify-end gap-2">
          <div
            v-if="chips.length"
            class="flex min-w-0 flex-1 flex-wrap gap-2"
            data-test="mobile-score-blurb-chips"
          >
            <ScoreChip
              v-for="chip in chips"
              :key="chip.title"
              :chip-title="chip.title"
              :chip-text="chip.text"
              :color="chip.color"
              :popup-location="'top'"
              :should-show-dialog="true"
              analytics-source="Mobile Conversation Tab"
              :score="scoreValue"
              :application-id="applicationId"
            />
          </div>

          <CopilotScoreFeedback
            v-if="hasScore"
            class="shrink-0"
            data-test="mobile-score-blurb-feedback"
            :job-applicant-id="applicationId"
            :copilot-score="scoreValue"
            :copilot-blurb="rawSummary"
            :copilot-chips="chips"
            alignment="right"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.score-summary :deep(ul) {
  @apply !list-disc;
  /* Tailwind preflight zeroes <ul> padding, so list-disc alone leaves the markers
     flush against the card edge. Restore the indent (1.5em ≈ the Figma list spec of
     1.5x the 14px text size) so the discs sit inside the card with a hanging indent. */
  padding-inline-start: 1.5em;
}
</style>
